// src/pages/msme/MSMEProcessRACIHome.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

// Dummy Data
const dummyProcesses = Array.from({ length: 22 }).map((_, i) => ({
  id: i + 1,
  processName: `Process ${i + 1}`,
  departments: ["Finance", "Operations", "HR"].slice(0, (i % 3) + 1),
  task: `Task for process ${i + 1}`,
  owner: ["Finance", "Operations", "HR"][i % 3],
  R: ["John", "Suresh", "Ramesh"][i % 3],
  A: ["Priya", "Anita", "Meena"][i % 3],
  C: ["Ramesh", "John", "Suresh"][i % 3],
  I: ["Anita", "Meena", "Priya"][i % 3],
}));

// RACI Color Map
const raciColors = {
  R: "bg-green-600",
  A: "bg-blue-600",
  C: "bg-yellow-500",
  I: "bg-gray-500",
};

const MSMEProcessRACIHome = () => {
  const [processes, setProcesses] = useState(dummyProcesses);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 5;
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const initialFormData = {
    processName: "",
    departments: "",
    task: "",
    owner: "",
    R: "",
    A: "",
    C: "",
    I: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this process?")) {
      setProcesses((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const handleEdit = (id) => {
    const process = processes.find((p) => p.id === id);
    if (process) {
      setFormData({
        ...initialFormData,
        processName: process.processName,
        departments: process.departments.join(", "),
        task: process.task,
        owner: process.owner,
        R: process.R,
        A: process.A,
        C: process.C,
        I: process.I,
      });
      setEditingId(id);
      setShowModal(true);
    }
  };

  const handleAdd = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { processName, task, owner, R, A, C, I, departments } = formData;
    const deptArray = departments
      ? departments.split(",").map((d) => d.trim()).filter((d) => d)
      : [];
    const newProcess = {
      id: editingId || Math.max(0, ...processes.map((p) => p.id)) + 1,
      processName,
      departments: deptArray,
      task,
      owner,
      R,
      A,
      C,
      I,
    };
    if (editingId) {
      setProcesses((prev) =>
        prev.map((p) => (p.id === editingId ? newProcess : p))
      );
    } else {
      setProcesses((prev) => [...prev, newProcess]);
    }
    setShowModal(false);
    if (!editingId) {
      setFormData(initialFormData);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (!editingId) {
      setFormData(initialFormData);
    }
  };

  const handleExport = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        "Process,Departments,Task,Owner,R,A,C,I",
      ].concat(
        processes.map(
          (p) =>
            `${p.processName},"${p.departments.join("|")}",${p.task},${p.owner},${p.R},${p.A},${p.C},${p.I}`
        )
      ).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "msme_process_raci.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtering
  const filtered = processes.filter((p) =>
    p.processName.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <div className="flex flex-row sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
        <h1 className="text-xl font-bold">MSME Process & RACI Dashboard</h1>
        <div className="flex flex-row gap-2 flex-wrap">
          <button
            onClick={handleAdd}
            className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Process
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded"
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search Process..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full sm:w-1/2 px-3 py-2 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full border border-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left border-b border-gray-700">
                Process
              </th>
              <th className="px-3 py-2 text-left border-b border-gray-700">
                Departments
              </th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Task</th>
              <th className="px-3 py-2 text-left border-b border-gray-700">Owner</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">R</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">A</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">C</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">I</th>
              <th className="px-3 py-2 text-center border-b border-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((p) => (
              <tr key={p.id} className="hover:bg-gray-800">
                <td className="px-3 py-2 border-b border-gray-700">
                  {p.processName}
                </td>
                <td className="px-3 py-2 border-b border-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {p.departments.map((d, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-600 px-2 py-0.5 rounded-full text-xs"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 border-b border-gray-700">{p.task}</td>
                <td className="px-3 py-2 border-b border-gray-700">{p.owner}</td>
                <td className="px-3 py-2 border-b border-gray-700 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${raciColors.R}`}>
                    {p.R}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-gray-700 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${raciColors.A}`}>
                    {p.A}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-gray-700 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${raciColors.C}`}>
                    {p.C}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-gray-700 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${raciColors.I}`}>
                    {p.I}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-gray-700 flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(p.id)}
                    className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="p-1 bg-red-600 hover:bg-red-700 rounded"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td
                  colSpan="9"
                  className="px-3 py-4 text-center text-gray-400"
                >
                  No processes found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden mt-4 space-y-4">
        {paginated.map((p) => (
          <div
            key={p.id}
            className="bg-gray-800 p-4 rounded shadow border border-gray-700"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold">{p.processName}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(p.id)}
                  className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="p-1 bg-red-600 hover:bg-red-700 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {p.departments.map((d, idx) => (
                <span
                  key={idx}
                  className="bg-purple-600 px-2 py-0.5 rounded-full text-xs"
                >
                  {d}
                </span>
              ))}
            </div>
            <p>
              <span className="font-semibold">Task:</span> {p.task}
            </p>
            <p>
              <span className="font-semibold">Owner:</span> {p.owner}
            </p>
            <div className="flex justify-between mt-2 text-sm">
              <span className={`px-2 py-0.5 rounded ${raciColors.R}`}>
                R: {p.R}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.A}`}>
                A: {p.A}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.C}`}>
                C: {p.C}
              </span>
              <span className={`px-2 py-0.5 rounded ${raciColors.I}`}>
                I: {p.I}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                {editingId ? "Edit Process" : "Add Process"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Process Name
                  </label>
                  <input
                    type="text"
                    value={formData.processName}
                    onChange={(e) =>
                      setFormData({ ...formData, processName: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Departments (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.departments}
                    onChange={(e) =>
                      setFormData({ ...formData, departments: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Task</label>
                  <input
                    type="text"
                    value={formData.task}
                    onChange={(e) =>
                      setFormData({ ...formData, task: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Owner</label>
                  <input
                    type="text"
                    value={formData.owner}
                    onChange={(e) =>
                      setFormData({ ...formData, owner: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">R</label>
                    <input
                      type="text"
                      value={formData.R}
                      onChange={(e) =>
                        setFormData({ ...formData, R: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">A</label>
                    <input
                      type="text"
                      value={formData.A}
                      onChange={(e) =>
                        setFormData({ ...formData, A: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">C</label>
                    <input
                      type="text"
                      value={formData.C}
                      onChange={(e) =>
                        setFormData({ ...formData, C: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">I</label>
                    <input
                      type="text"
                      value={formData.I}
                      onChange={(e) =>
                        setFormData({ ...formData, I: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                  >
                    {editingId ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-2 mt-4">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faChevronLeft} /> Prev
        </button>
        <span>Page {currentPage} / {totalPages}</span>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
};

export default MSMEProcessRACIHome;